import React, { useEffect, useRef, useState } from 'react';
import * as UTIF from 'utif';
import { openDocumentInNewTab } from './documentUtils';

/**
 * TiffPreview Component
 * Renders TIFF images using the UTIF library
 * Converts TIFF to canvas for display in JSON viewer
 *
 * @param {string} value - The data URI of the TIFF image
 * @param {string} docType - The document type (should be 'tiff')
 */
const TiffPreview = ({ value, docType }) => {
  const canvasRef = useRef(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const renderTIFF = async () => {
      try {
        console.log('🖼️ Starting TIFF render...');
        setLoading(true);
        setError(null);

        // Wait for canvas to be ready
        await new Promise((resolve) => setTimeout(resolve, 50));

        const canvas = canvasRef.current;
        if (!canvas) {
          console.error('Canvas not available');
          throw new Error('Canvas not ready');
        }

        // Extract base64 data from data URI
        const base64Data = value.split(',')[1];
        if (!base64Data) {
          throw new Error('Invalid TIFF data format');
        }

        // Convert base64 to array buffer
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const buffer = bytes.buffer;

        // Decode TIFF
        console.log('🔄 Decoding TIFF...');
        const ifds = UTIF.decode(buffer);
        if (!ifds.length) throw new Error('No image data found in TIFF');

        UTIF.decodeImage(buffer, ifds[0]);
        const rgba = UTIF.toRGBA8(ifds[0]);

        canvas.width = ifds[0].width;
        canvas.height = ifds[0].height;

        const ctx = canvas.getContext('2d');
        const imageData = new ImageData(
          new Uint8ClampedArray(rgba),
          ifds[0].width,
          ifds[0].height
        );
        ctx.putImageData(imageData, 0, 0);

        console.log('✅ TIFF rendered successfully');
        setLoading(false);
      } catch (err) {
        console.error('❌ TIFF render error:', err);
        setError(err.message || 'Failed to render TIFF');
        setLoading(false);
      }
    };

    if (value && canvasRef.current) {
      renderTIFF();
    }
  }, [value]);

  if (error) {
    return (
      <div
        className="d-inline-flex gap-2 px-2 py-2 border cursor-pointer mt-2 br_7 align-items-center"
        style={{
          backgroundColor: '#FFEBEE',
          maxWidth: '200px',
        }}
        onClick={() => openDocumentInNewTab(value, docType)}
        title="Failed to preview. Click to open in new tab"
      >
        <span className="font_24">⚠️</span>
        <div className="d-flex flex-column overflow-hidden">
          <span className="font_11 font_500" style={{ color: '#666' }}>
            TIFF Image
          </span>
          <span className="font_10" style={{ color: '#999' }}>
            {error}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      {loading && (
        <div
          style={{
            fontSize: '11px',
            color: '#999',
            fontStyle: 'italic',
            padding: '8px',
          }}
        >
          Loading TIFF...
        </div>
      )}
      <canvas
        ref={canvasRef}
        className="cursor-pointer mt-2"
        style={{
          maxWidth: '90px',
          maxHeight: '90px',
          objectFit: 'contain',
          display: loading ? 'none' : 'block',
        }}
        onClick={() => openDocumentInNewTab(value, docType)}
        title="Click to open in new tab"
      />
    </div>
  );
};

export default TiffPreview;
